import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, UserPlus, Download, Edit } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import EditTestModal from '../../components/EditTestModal';

function TestDetail() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    fetchTestDetails();
    fetchUsers();
    fetchBatches();
  }, [testId]);

  useEffect(() => {
    filterUsersByBatch();
  }, [selectedBatch, users]);

  const fetchTestDetails = async () => {
    try {
      const response = await api.get(`/tests/${testId}`);
      setTest(response.data);
    } catch (error) {
      console.error('Error fetching test:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/for-assignment');
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const filterUsersByBatch = () => {
    if (selectedBatch === 'all') {
      setFilteredUsers(users);
    } else if (selectedBatch === 'no-batch') {
      setFilteredUsers(users.filter(u => !u.batch_id));
    } else {
      setFilteredUsers(users.filter(u => u.batch_id === selectedBatch));
    }
    // Clear selections when changing batch
    setSelectedUsers([]);
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const handlePublish = async () => {
    try {
      await api.put(`/tests/${testId}/status`, null, { params: { status: 'published' } });
      toast.success('Test published successfully');
      fetchTestDetails();
    } catch (error) {
      console.error('Error publishing test:', error);
    }
  };

  const handleAssignUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    try {
      await api.post('/test-assignments', {
        test_id: testId,
        user_ids: selectedUsers,
      });
      toast.success('Test assigned successfully');
      setAssignOpen(false);
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error assigning test:', error);
    }
  };

  const exportResults = async (format) => {
    try {
      const response = await api.get(`/export/test/${testId}/${format}`, {
        responseType: format === 'excel' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const data = response.data;
        const blob = new Blob([data.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `test_results_${testId}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else if (format === 'excel') {
        // Response is already a blob
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `test_results_${testId}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Error exporting results:', error);
      toast.error('Failed to export results');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/tests')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tests
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900" data-testid="test-title">{test?.title}</h1>
              <Badge variant={test?.status === 'published' ? 'success' : 'secondary'}>
                {test?.status}
              </Badge>
            </div>
            <p className="text-gray-600">{test?.description}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(true)}
              data-testid="edit-test-button"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Test
            </Button>
            {test?.status === 'draft' && (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handlePublish}
                data-testid="publish-button"
              >
                Publish Test
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setAssignOpen(true)}
              data-testid="assign-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Assign Users
            </Button>
            <Button
              variant="outline"
              onClick={() => exportResults('csv')}
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => exportResults('excel')}
              data-testid="export-excel-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-1">Duration</p>
            <p className="text-2xl font-bold text-gray-900">{test?.config?.duration} mins</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-1">Questions</p>
            <p className="text-2xl font-bold text-gray-900">{test?.question_ids?.length || 0}</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-1">Total Points</p>
            <p className="text-2xl font-bold text-gray-900">{test?.total_points || 0}</p>
          </Card>
        </div>

        {(test?.start_time || test?.end_time) && (
          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold mb-3">Schedule</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Start Date:</span>
                <span className="ml-2 font-medium" data-testid="test-start-time">
                  {test?.start_time ? new Date(test.start_time).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Not set'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">End Date:</span>
                <span className="ml-2 font-medium" data-testid="test-end-time">
                  {test?.end_time ? new Date(test.end_time).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Not set'}
                </span>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Attempts Allowed:</span>
              <span className="ml-2 font-medium">{test?.config?.attempts_allowed}</span>
            </div>
            <div>
              <span className="text-gray-600">Pass Percentage:</span>
              <span className="ml-2 font-medium">{test?.config?.pass_percentage}%</span>
            </div>
            <div>
              <span className="text-gray-600">Negative Marking:</span>
              <span className="ml-2 font-medium">{test?.config?.negative_marking}</span>
            </div>
            <div>
              <span className="text-gray-600">Proctoring:</span>
              <span className="ml-2 font-medium">{test?.config?.proctoring_enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </Card>

        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Assign Test to Users</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Step 1: Select Batch */}
              <div className="space-y-2">
                <Label className="font-semibold">Step 1: Filter by Batch (Optional)</Label>
                <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="no-batch">Users without Batch</SelectItem>
                    {batches.map((batch) => (
                      <SelectItem key={batch.id} value={batch.id}>
                        {batch.name} {batch.year && `(${batch.year})`} - {batch.user_count} users
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 2: Select Users */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Step 2: Select Users</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  Showing {filteredUsers.length} user(s) • {selectedUsers.length} selected
                </div>
                <div className="max-h-80 overflow-y-auto space-y-2 border rounded-lg p-3">
                  {filteredUsers.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No users found in this selection</p>
                  ) : (
                    filteredUsers.map((user) => (
                      <div key={user.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <Checkbox
                          id={user.id}
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedUsers([...selectedUsers, user.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                            }
                          }}
                        />
                        <Label htmlFor={user.id} className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                            {user.batch_id && (
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                {batches.find(b => b.id === user.batch_id)?.name || 'Batch'}
                              </span>
                            )}
                          </div>
                        </Label>
                      </div>
                    ))
                  )}
                </div>
                <Button onClick={handleAssignUsers} className="w-full" data-testid="confirm-assign-button">
                  Assign ({selectedUsers.length} selected)
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <EditTestModal
          test={test}
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={fetchTestDetails}
        />
      </div>
    </Layout>
  );
}

export default TestDetail;